import {
  DataModel, SerializationContext, SerializedData, DataModelBuilder,
  isSameList, isSameData, generateUniqueDatabaseId,
} from '../../dto/dto.js';
import { Sector } from './sector.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { SeatingElement } from './seating-element.js';
import { SeatRow } from './seat-row.js';
import { Seat } from './seat.js';
import { SeatingLabel } from './seating-label.js';
import { CameraConstraints } from './camera-constraints.js';
import { SeatingLayoutSerializer } from '../../../serialization/seating-layout-serializer.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { EventProfile } from '../event-profile.js';
import { SeatingRoom } from './seating-room.js';
import { LegacySeatingLayout } from './legacy-seating-layout.js';

export class SeatingLayout extends DataModel {
  static readonly TYPE_ID: string = '716d9172fc4a49b6af0f62281094bf60';

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly roomId: string,
    public readonly buildingId: string,
    public readonly name: string,
    public readonly backgroundImage: string | null,
    public readonly children: SeatingElement[],
    public readonly cameraConstraints: CameraConstraints,
  ) {
    super();
  }

  get $typeId(): string {
    return SeatingLayout.TYPE_ID;
  }

  static fromRoom(event: EventProfile, room: SeatingRoom, layoutId?: string | null): SeatingLayout {
    return new SeatingLayout(
        layoutId ?? generateUniqueDatabaseId(),
        event.businessRef.clone(),
        event.uniqueId,
        room.uniqueId,
        room.buildingId,
        room.name,
        room.backgroundImage,
        room.children.map((e) => e.clone(null)),
        room.cameraConstraints.clone(),
    );
  }

  static fromLegacy(event: EventProfile, legacyLayout: LegacySeatingLayout): SeatingLayout {
    return new SeatingLayout(
        generateUniqueDatabaseId(),
        event.businessRef.clone(),
        event.uniqueId,
        legacyLayout.uniqueId,
        legacyLayout.uniqueId,
        legacyLayout.name,
        legacyLayout.backgroundImage,
        legacyLayout.children.map((e) => e.clone(null)),
        legacyLayout.cameraConstraints.clone(),
    );
  }

  static fromMap(context: SerializationContext, data: SerializedData): SeatingLayout {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.fromJson(context, data);
      case 'binary':
        return this.fromBinary(context, data);
      case 'base64':
        return this.fromBase64(context, data);
    }

    throw Error('Unknown seating layout serialization format: ' + format);
  }

  toMap(context: SerializationContext): SerializedData {
    const format = context.storage.get<string>('format') ?? 'binary';
    switch(format) {
      case 'json':
        return this.toJson(context);
      case 'binary':
        return this.toBinary(context);
      case 'base64':
        return this.toBase64(context);
    }

    throw Error('Unknown seating layout serialization format: ' + format);
  }

  clone(): this {
    return new SeatingLayout(
        this.uniqueId,
        this.businessRef.clone(),
        this.eventId,
        this.roomId,
        this.buildingId,
        this.name,
        this.backgroundImage,
        this.children.map((e) => e.clone(null)),
        this.cameraConstraints.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        isSameData(this.businessRef, other.businessRef) &&
        this.eventId === other.eventId &&
        this.roomId === other.roomId &&
        this.buildingId === other.buildingId &&
        this.name === other.name &&
        this.backgroundImage === other.backgroundImage &&
        isSameList(this.children, other.children, (a, b) => isSameData(a, b)) &&
        isSameData(this.cameraConstraints, other.cameraConstraints);
  }

  static fromJson(context: SerializationContext, data: SerializedData): SeatingLayout {
    const decodeChild = (childData: SerializedData): SeatingElement => {
      const typeId = MapUtils.getNumber(childData, '$typeId');
      switch(typeId) {
        case Sector.TYPE_ID:
          return Sector.fromJson(childData);
        case SeatRow.TYPE_ID:
          return SeatRow.fromJson(childData);
        case Seat.TYPE_ID:
          return Seat.fromJson(childData);
        case SeatingLabel.TYPE_ID:
          return SeatingLabel.fromJson(childData);
      }

      throw Error(`Unknown SeatingElement of type: ${typeId}`);
    };

    return new SeatingLayout(
        MapUtils.get<string>(data, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(data, 'businessRef')),
        MapUtils.get<string>(data, 'eventId'),
        MapUtils.get<string>(data, 'roomId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        MapUtils.get<SerializedData[]>(data, 'children')
            .map((childData) => decodeChild(childData)),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
    );
  }

  toJson(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'roomId': this.roomId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': this.children.map((child) => child.toJson()),
      'cameraConstraints': this.cameraConstraints.toJson(),
    };
  }

  static fromBinary(context: SerializationContext, data: SerializedData): SeatingLayout {
    return new SeatingLayout(
        MapUtils.get<string>(data, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(data, 'businessRef')),
        MapUtils.get<string>(data, 'eventId'),
        MapUtils.get<string>(data, 'roomId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        SeatingLayoutSerializer.deserialize(context.fromBinaryBlob(MapUtils.getRaw(data, 'children'))),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
    );
  }

  toBinary(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'roomId': this.roomId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': context.toBinaryBlob(SeatingLayoutSerializer.serialize(this.children)),
      'cameraConstraints': this.cameraConstraints.toJson(),
    };
  }

  static fromBase64(context: SerializationContext, data: SerializedData): SeatingLayout {
    return new SeatingLayout(
        MapUtils.get<string>(data, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(data, 'businessRef')),
        MapUtils.get<string>(data, 'eventId'),
        MapUtils.get<string>(data, 'roomId'),
        MapUtils.get<string>(data, 'buildingId'),
        MapUtils.get<string>(data, 'name'),
        MapUtils.getOrNull<string>(data, 'backgroundImage'),
        SeatingLayoutSerializer.deserialize(context.fromBase64(MapUtils.get<string>(data, 'children'))),
        CameraConstraints.fromJson(MapUtils.get<SerializedData>(data, 'cameraConstraints')),
    );
  }

  toBase64(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'roomId': this.roomId,
      'buildingId': this.buildingId,
      'name': this.name,
      'backgroundImage': this.backgroundImage,
      'children': context.toBase64(SeatingLayoutSerializer.serialize(this.children)),
      'cameraConstraints': this.cameraConstraints.toJson(),
    };
  }

  findSeatById(seatId: number): Seat | null {
    for(const child of this.children) {
      const seat = this.findSeatByIdRecursive(child, seatId);
      if(seat != null) {
        return seat;
      }
    }

    return null;
  }

  private findSeatByIdRecursive(elem: SeatingElement, seatId: number): Seat | null {
    if(elem instanceof Seat && elem.uniqueId === seatId) {
      return elem;
    }
    else if(elem instanceof SeatRow) {
      for(const seat of elem.seats) {
        if(seat.uniqueId === seatId) {
          return seat;
        }
      }
    }
    else if(elem instanceof Sector) {
      for(const child of elem.children) {
        const seat = this.findSeatByIdRecursive(child, seatId);
        if(seat != null) {
          return seat;
        }
      }
    }

    return null;
  }
}

export class SeatingLayoutBuilder extends DataModelBuilder<SeatingLayout> {
  build(context: SerializationContext, map: SerializedData): SeatingLayout {
    return SeatingLayout.fromMap(context, map);
  }
}
